import React, { useState, Component } from 'react';
/**
 * AnswerComparison — Shows user answer vs correct answer in various modes.
 *
 * Modes: full (side-by-side cards), compact (stacked), inline (minimal).
 * Supports LaTeX rendering and long-answer truncation.
 *
 * Co-located in practice/ domain (CL-1 s64).
 */
import { motion } from 'framer-motion';
import { Check, X, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
// ── Types ────────────────────────────────────────────────────
interface AnswerComparisonProps {
  userAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  mode?: 'full' | 'compact' | 'inline';
}
// ── Truncation Helper ────────────────────────────────────────
const TRUNCATE_LIMIT = 50;
function TruncatedAnswer({
  answer,
  variant
}: {
  answer: string;
  variant: 'correct' | 'incorrect';
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const needsTruncation = answer.length > TRUNCATE_LIMIT;
  const displayText = needsTruncation && !isExpanded ? answer.slice(0, TRUNCATE_LIMIT) + '...' : answer;
  const styles = variant === 'correct' ? {
    bg: 'bg-emerald-50 border-emerald-200',
    text: 'text-emerald-900',
    btn: 'text-emerald-600',
    border: 'border-emerald-100'
  } : {
    bg: 'bg-rose-50 border-rose-200',
    text: 'text-rose-900',
    btn: 'text-rose-600',
    border: 'border-rose-100'
  };
  return <div className={`rounded-xl border ${styles.bg} overflow-hidden`} data-id="element-6441">
      <div className="px-3 py-2.5" data-id="element-6442">
        <div className={`font-bold text-sm ${styles.text}`} data-id="element-6443">
          <LatexRenderer content={displayText} data-id="element-6444" />
        </div>
      </div>
      {needsTruncation && <button onClick={() => setIsExpanded(!isExpanded)} className={`w-full px-3 py-1.5 text-[10px] font-bold ${styles.btn} bg-white/50 border-t ${styles.border} flex items-center justify-center gap-1 hover:bg-white/80 transition-colors`} data-id="element-6445">
          {isExpanded ? <>
              Toon minder <ChevronUp className="w-3 h-3" data-id="element-6446" />
            </> : <>
              Toon meer <ChevronDown className="w-3 h-3" data-id="element-6447" />
            </>}
        </button>}
    </div>;
}
// ── Component ────────────────────────────────────────────────
export function AnswerComparison({
  userAnswer,
  correctAnswer,
  isCorrect,
  mode = 'full'
}: AnswerComparisonProps) {
  // ── Inline Mode ──
  if (mode === 'inline') {
    if (isCorrect) {
      return <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="flex justify-center" data-id="element-6448">
          <div className="inline-flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3 shadow-sm max-w-full" data-id="element-6449">
            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0" data-id="element-6450">
              <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-6451" />
            </div>
            <div className="font-bold text-emerald-900 text-base truncate max-w-[220px]" data-id="element-6452">
              <LatexRenderer content={userAnswer.length > TRUNCATE_LIMIT ? userAnswer.slice(0, TRUNCATE_LIMIT) + '...' : userAnswer} data-id="element-6453" />
            </div>
          </div>
        </motion.div>;
    }
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="space-y-2.5 px-2" data-id="element-6454">
        <div className="flex items-start gap-2" data-id="element-6455">
          <div className="w-5 h-5 rounded-full bg-rose-200 flex items-center justify-center text-rose-700 flex-shrink-0 mt-2.5" data-id="element-6456">
            <X className="w-3.5 h-3.5" strokeWidth={3} data-id="element-6457" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-6458">
            <p className="text-[10px] font-bold uppercase text-rose-500 mb-1" data-id="element-6459">
              Jouw antwoord
            </p>
            <TruncatedAnswer answer={userAnswer} variant="incorrect" data-id="element-6460" />
          </div>
        </div>
        <div className="flex items-start gap-2" data-id="element-6461">
          <div className="w-5 h-5 rounded-full bg-emerald-200 flex items-center justify-center text-emerald-700 flex-shrink-0 mt-2.5" data-id="element-6462">
            <Check className="w-3.5 h-3.5" strokeWidth={3} data-id="element-6463" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-6464">
            <p className="text-[10px] font-bold uppercase text-emerald-600 mb-1" data-id="element-6465">
              Correct
            </p>
            <TruncatedAnswer answer={correctAnswer} variant="correct" data-id="element-6466" />
          </div>
        </div>
      </motion.div>;
  }
  // ── Compact Mode ──
  if (mode === 'compact') {
    return <div className="grid gap-2" data-id="element-6467">
        <div className={`p-3 rounded-xl border-2 ${isCorrect ? 'border-emerald-200 bg-emerald-50' : 'border-rose-100 bg-rose-50'}`} data-id="element-6468">
          <div className="text-[10px] font-black uppercase mb-1 opacity-60" data-id="element-6469">
            Jouw antwoord
          </div>
          <div className="font-bold text-slate-900 text-sm" data-id="element-6470">
            <LatexRenderer content={userAnswer} data-id="element-6471" />
          </div>
        </div>
        {!isCorrect && <div className="p-3 rounded-xl border-2 border-emerald-200 bg-emerald-50" data-id="element-6472">
            <div className="text-[10px] font-black uppercase mb-1 text-emerald-700" data-id="element-6473">
              Goede antwoord
            </div>
            <div className="font-bold text-emerald-900 text-sm" data-id="element-6474">
              <LatexRenderer content={correctAnswer} data-id="element-6475" />
            </div>
          </div>}
      </div>;
  }
  // ── Full Mode ──
  if (isCorrect) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1]
    }} className="bg-emerald-50 rounded-3xl p-6 border-2 border-emerald-200 shadow-sm" data-id="element-6476">
        <div className="flex items-center gap-4" data-id="element-6477">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: [0, 1.2, 1]
        }} transition={{
          delay: 0.1,
          duration: 0.4,
          times: [0, 0.6, 1]
        }} className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-md flex-shrink-0" data-id="element-6478">
            <Check className="w-7 h-7 text-white" strokeWidth={3} data-id="element-6479" />
          </motion.div>
          <div data-id="element-6480">
            <div className="text-xs font-black uppercase tracking-wider text-emerald-600 mb-1" data-id="element-6481">
              Jouw antwoord
            </div>
            <div className="font-black text-2xl text-emerald-900" data-id="element-6482">
              <LatexRenderer content={userAnswer} data-id="element-6483" />
            </div>
          </div>
        </div>
      </motion.div>;
  }
  return <div className="flex flex-col md:flex-row gap-3 items-stretch" data-id="element-6484">
      <motion.div initial={{
      opacity: 0,
      x: -16
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className="flex-1" data-id="element-6485">
        <div className="bg-rose-50 rounded-3xl p-6 border-2 border-rose-200 h-full shadow-sm" data-id="element-6486">
          <div className="flex items-center gap-3 mb-3" data-id="element-6487">
            <div className="w-9 h-9 rounded-xl bg-rose-200 flex items-center justify-center text-rose-700 shadow-sm flex-shrink-0" data-id="element-6488">
              <X className="w-5 h-5" strokeWidth={3} data-id="element-6489" />
            </div>
            <span className="text-xs font-black uppercase tracking-wider text-rose-600" data-id="element-6490">
              Jouw antwoord
            </span>
          </div>
          <div className="font-black text-2xl text-rose-900 pl-12" data-id="element-6491">
            <LatexRenderer content={userAnswer} data-id="element-6492" />
          </div>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 0.15,
      duration: 0.3
    }} className="hidden md:flex items-center justify-center flex-shrink-0" data-id="element-6493">
        <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center" data-id="element-6494">
          <ArrowRight className="w-5 h-5 text-slate-400" strokeWidth={2.5} data-id="element-6495" />
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      x: 16
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.1,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className="flex-1" data-id="element-6496">
        <div className="bg-emerald-50 rounded-3xl p-6 border-2 border-emerald-200 h-full shadow-sm" data-id="element-6497">
          <div className="flex items-center gap-3 mb-3" data-id="element-6498">
            <div className="w-9 h-9 rounded-xl bg-emerald-200 flex items-center justify-center text-emerald-700 shadow-sm flex-shrink-0" data-id="element-6499">
              <Check className="w-5 h-5" strokeWidth={3} data-id="element-6500" />
            </div>
            <span className="text-xs font-black uppercase tracking-wider text-emerald-600" data-id="element-6501">
              Het juiste antwoord
            </span>
          </div>
          <div className="font-black text-2xl text-emerald-900 pl-12" data-id="element-6502">
            <LatexRenderer content={correctAnswer} data-id="element-6503" />
          </div>
        </div>
      </motion.div>
    </div>;
}